/* eslint-disable max-len */
import React, { ReactNode } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { theme, toRem } from 'design';
import InputStyles from '../Input/InputStyles';

const BUTTON_NEXT_IMAGE =
  "\"data:image/svg+xml,%3Csvg width='11' height='10' viewBox='0 0 11 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.34171 0.583947C6.14645 0.388684 5.82987 0.388684 5.63461 0.583947C5.43934 0.779209 5.43934 1.09579 5.63461 1.29105L8.84237 4.49882H1.25C0.973858 4.49882 0.75 4.72268 0.75 4.99882C0.75 5.27496 0.973858 5.49882 1.25 5.49882H8.84238L5.63461 8.7066C5.43934 8.90186 5.43934 9.21844 5.63461 9.4137C5.82987 9.60897 6.14645 9.60897 6.34171 9.4137L10.403 5.35238C10.4968 5.25861 10.5495 5.13143 10.5495 4.99883C10.5495 4.86622 10.4968 4.73904 10.403 4.64527L6.34171 0.583947Z' fill='%23687081'/%3E%3C/svg%3E%0A\"";
const BUTTON_PREV_IMAGE =
  "\"data:image/svg+xml,%3Csvg width='11' height='10' viewBox='0 0 11 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.45777 9.4137C4.65303 9.60897 4.96962 9.60897 5.16488 9.4137C5.36014 9.21844 5.36014 8.90186 5.16488 8.7066L1.95711 5.49883L9.54949 5.49883C9.82563 5.49883 10.0495 5.27497 10.0495 4.99883C10.0495 4.72269 9.82563 4.49883 9.54949 4.49883L1.9571 4.49883L5.16488 1.29105C5.36014 1.09579 5.36014 0.779209 5.16488 0.583946C4.96962 0.388685 4.65304 0.388684 4.45777 0.583946L0.396446 4.64527C0.302679 4.73904 0.25 4.86622 0.25 4.99883C0.25 5.13143 0.302679 5.25861 0.396446 5.35238L4.45777 9.4137Z' fill='%23687081'/%3E%3C/svg%3E%0A\"";
const BUTTON_RADIUS = 2.75;
const DAY_SIZE = 34;
const HEADER_HEIGHT = 44;
const HEIGHT_DEFAULT = 44;
const ICON_WIDTH = 35.75;
const NAV_BUTTON_SIZE = 22;
const PADDING = 11;
const ZINDEX_CONTENT = theme.zIndex.content;
const ZINDEX_NAVBAR = theme.zIndex.content + 1;

const CALENDAR_PADDING = 11;
const CALENDAR_WIDTH = DAY_SIZE * 7 + CALENDAR_PADDING * 2;
const MONTH_MARGIN = 16;
const CALENDAR_RANGE_WIDTH = DAY_SIZE * 7 * 2 + CALENDAR_PADDING * 2 + MONTH_MARGIN;

interface IDatePickerLayoutProps {
  children?: ReactNode;
  className?: string;
  fluidWidth?: boolean;
  height?: number;
  message?: any;
  ref?: any;
  width?: number;
  onClick?: () => void;
}

const DateLayout = styled.div<IDatePickerLayoutProps>`
  position: relative;
  z-index: ${theme.zIndex.overlay};
  display: flex;
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: ${toRem(HEIGHT_DEFAULT)};

  ${({ height }) =>
    height &&
    css`
      height: ${toRem(height)};

      .DayPickerInput {
        input {
          height: ${toRem(height)};
        }
      }
    `}

  ${({ width }) =>
    width &&
    css`
      width: ${toRem(width)};
    `}
`;

export const DatePickerLayout = ({ children, className, height, ref, width }: IDatePickerLayoutProps) => (
  <DateLayout className={className} width={width} height={height} ref={ref}>
    {children}
  </DateLayout>
);

export const RangeClassName = 'Selectable';

const RDPContainerStyles = css`
  // Parent Container
  .DayPicker {
    position: relative;
    z-index: ${ZINDEX_CONTENT};
    display: flex;
    width: 100%;
    padding: 0 0;
    font-family: ${theme.font.family.euclid};

    &--interactionDisabled .DayPicker-Day {
      cursor: default;
    }
  }

  // Content Wrapper
  .DayPicker-wrapper {
    position: relative;
    display: flex;
    flex-shrink: 0;
    flex-flow: column nowrap;
    width: ${toRem(CALENDAR_WIDTH)};
    padding: 0 ${toRem(CALENDAR_PADDING)};
    border-radius: ${toRem(5.5)};
    background-color: ${theme.color.white};
    box-shadow: ${theme.shadow.dialog};
    user-select: none;
  }
`;

const NewRangePickerStyles = css`
  && {
    .${RangeClassName} {
      .DayPicker-wrapper {
        flex-flow: column nowrap;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100%;
        padding: 0 0;
        border-radius: 0;
        background-color: none;
        box-shadow: none;

        @media (min-width: ${theme.breakpoint.med}px) {
          flex-flow: row nowrap;
          justify-content: flex-start;
          align-items: flex-start;
          width: ${toRem(CALENDAR_RANGE_WIDTH)};
        }
      }

      .DayPicker-Months {
        padding: 0 ${toRem(11)};
      }

      .DayPicker-Month {
        width: 100%;
        min-width: ${toRem(CALENDAR_WIDTH)};

        &:first-child {
          margin-bottom: ${toRem(MONTH_MARGIN)};
        }

        @media (min-width: ${theme.breakpoint.med}px) {
          width: auto;
          min-width: auto;

          &:first-child {
            margin-right: ${toRem(MONTH_MARGIN)};
            margin-bottom: 0;
          }
        }
      }
    }
  }
`;

const RDPContentStyles = css`
  // Nav Button Container
  .DayPicker-NavBar {
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${ZINDEX_NAVBAR};
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    width: auto;
    height: ${toRem(HEADER_HEIGHT)};
    padding: 0 ${toRem(PADDING)} 0 0;
  }

  // Nav Button
  .DayPicker-NavButton {
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    width: ${toRem(NAV_BUTTON_SIZE)};
    height: ${toRem(NAV_BUTTON_SIZE)};
    border-radius: ${toRem(BUTTON_RADIUS)};
    background-color: ${theme.color.neutral[100]};
    background-position: center;
    background-size: 50%;
    background-repeat: no-repeat;
    color: ${theme.color.neutral[900]};
    cursor: pointer;
    transition: background-color ${theme.transition.button.fast};

    &:hover {
      background-color: ${theme.color.neutral[100]};
    }

    &--prev {
      margin-right: ${toRem(5.5)};
      background-image: url(${BUTTON_PREV_IMAGE});
    }

    &--next {
      background-image: url(${BUTTON_NEXT_IMAGE});
    }

    &--interactionDisabled {
      display: none;
    }
  }

  // Current Month Container
  // Month + Year + Weekdays + Body
  .DayPicker-Months {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 0;
  }

  // Title + Weekdays + Body
  .DayPicker-Month {
    display: table;
    margin-bottom: ${toRem(11)};
    border-spacing: 0;
    user-select: none;
  }

  // Month + Year Header
  // Nested div is the Month + Year Text
  .DayPicker-Caption {
    display: table-caption;

    > div {
      position: relative;
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;
      width: auto;
      height: ${toRem(HEADER_HEIGHT)};
      padding: 0 ${toRem(PADDING)};
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
      font-family: ${theme.font.family.euclid};
      font-weight: ${theme.font.weight.medium};
      font-size: ${toRem(13.25)};
      line-height: 100%;
      color: ${theme.color.neutral[900]};
    }
  }

  // Weekday Row Container
  .DayPicker-Weekdays {
    position: relative;
    display: table-header-group;
  }

  // Weekday Row
  .DayPicker-WeekdaysRow {
    position: relative;
    display: table-row;
  }

  // Weekday Label
  .DayPicker-Weekday {
    position: relative;
    display: table-cell;
    vertical-align: middle;
    height: ${toRem(55)};
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
    font-size: ${toRem(13.25)};
    line-height: 100%;
    text-align: center;
    color: ${theme.color.neutral[800]};

    abbr[title] {
      position: relative;
      border-bottom: none;
      font: inherit;
      color: inherit;
      text-decoration: none;
    }
  }

  // Calendar Grid Container
  .DayPicker-Body {
    display: table-row-group;
  }

  // Calendar Grid Row
  .DayPicker-Week {
    display: table-row;
  }

  // Not currently using, unstyled
  .DayPicker-WeekNumber {
    display: table-cell;
    vertical-align: middle;
    cursor: pointer;
  }

  // Footer
  .DayPicker-Footer {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    height: ${toRem(HEADER_HEIGHT)};
    padding: 0 ${toRem(PADDING)};
    border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
  }

  // Footer Select Today Button
  .DayPicker-TodayButton {
    display: flex;
    flex-grow: 0;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    width: auto;
    height: ${toRem(22)};
    padding: 0 ${toRem(6.5)};
    border-radius: ${toRem(BUTTON_RADIUS)};
    background-color: ${theme.color.neutral[100]};
    font-weight: ${theme.font.weight.medium};
    font-size: ${toRem(11)};
    line-height: 100%;
    color: ${theme.color.neutral[800]};
    cursor: pointer;
    transition: background-color ${theme.transition.button.fast};

    &:hover {
      background-color: ${theme.color.neutral[200]};
      color: ${theme.color.neutral[900]};
    }
  }
`;

const DayBeforeStyles = css`
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  width: ${toRem(DAY_SIZE - 2)};
  height: ${toRem(DAY_SIZE - 2)};
  border-radius: ${toRem(BUTTON_RADIUS)};
  background-color: transparent;
`;

const RDPDayStyles = css`
  // Calendar Grid Item
  .DayPicker-Day {
    position: relative;
    z-index: 10;
    display: table-cell;
    vertical-align: middle;
    width: ${toRem(DAY_SIZE)};
    height: ${toRem(DAY_SIZE)};
    padding-top: ${toRem(1)};
    border-radius: ${toRem(BUTTON_RADIUS)};
    font-weight: ${theme.font.weight.regular};
    font-size: ${toRem(13.25)};
    line-height: 100%;
    text-align: center;
    color: ${theme.color.neutral[900]};
    cursor: pointer;
    overflow: hidden;

    &::before {
      ${DayBeforeStyles}
    }

    &:hover {
      &::before {
        background-color: ${theme.color.neutral[200]};
      }
    }

    &--today {
      &::before {
        border: ${toRem(1)} solid ${theme.color.secondary[400]};
      }
    }

    &--selected {
      color: ${theme.color.neutral[900]};

      &::before {
        background-color: ${theme.color.secondary[50]};
      }

      &:hover {
        color: ${theme.color.neutral[900]};
        &::before {
          border: ${toRem(1)} solid ${theme.color.secondary[300]};
          background-color: ${theme.color.secondary[400]};
        }
      }
    }

    // Start of range
    &--start {
      color: ${theme.color.white};

      &::before {
        background-color: ${theme.color.secondary[400]};
      }
    }

    // End of range
    &--end {
      color: ${theme.color.white};

      &::before {
        background-color: ${theme.color.secondary[400]};
      }
    }

    &--outside {
      background-color: transparent !important;
      opacity: 0;
      cursor: default;

      &::before {
        background-color: transparent;
      }
    }

    &--disabled {
      color: ${theme.color.neutral[400]};
      cursor: default;
    }

    // The middle selected days
    // Selected but not the start, not the end, and not outside
    &--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end) {
      color: ${theme.color.neutral[900]};
      &::before {
        background-color: ${theme.color.secondary[50]};
      }
    }

    // Selected but not disabled and not outside range
    /* &--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside):not(.DayPicker-Day--start):not(.DayPicker-Day--end) {
    } */

    // Not disabled, not selected, not outside range
    /* .DayPicker:not(.DayPicker--interactionDisabled)
      .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside) {
    } */
  }
`;

const RDPInputStyles = css`
  .DayPickerInput {
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    input {
      ${InputStyles}
      padding-right: ${toRem(ICON_WIDTH)};
    }

    &-OverlayWrapper {
      position: relative;
      display: flex;
      flex-flow: row nowrap;
      min-width: 100%;
    }

    &-Overlay {
      position: absolute;
      top: ${toRem(3)};
      right: 0;
      z-index: 1;
      width: ${toRem(CALENDAR_WIDTH)};
    }
  }
`;

export const ReactDayPickerStyles = createGlobalStyle`
  ${RDPContainerStyles}
  ${NewRangePickerStyles};
  ${RDPContentStyles}
  ${RDPDayStyles}
  ${RDPInputStyles}
`;
